import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, IceCream2, Settings, Inbox, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ScheduleManager from "@/components/admin/ScheduleManager";
import FlavorsManager from "@/components/admin/FlavorsManager";
import CartSettingsManager from "@/components/admin/CartSettingsManager";
import BookingsList from "@/components/admin/BookingsList";

const tabs = [
  { id: "schedule", label: "Schedule", icon: CalendarClock, comp: ScheduleManager },
  { id: "flavors", label: "Flavors", icon: IceCream2, comp: FlavorsManager },
  { id: "settings", label: "Cart & Map", icon: Settings, comp: CartSettingsManager },
  { id: "bookings", label: "Bookings", icon: Inbox, comp: BookingsList },
];

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("schedule");
  const [mobileOpen, setMobileOpen] = useState(false);

  const ActiveComp = tabs.find((t) => t.id === active).comp;

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  const Nav = () => (
    <nav className="flex-1 space-y-1.5">
      {tabs.map((t) => {
        const Icon = t.icon;
        const isActive = active === t.id;
        return (
          <button
            key={t.id}
            onClick={() => { setActive(t.id); setMobileOpen(false); }}
            data-testid={`admin-tab-${t.id}`}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
              isActive ? "bg-sc-gold text-sc-espresso" : "text-sc-cream/70 hover:bg-white/10 hover:text-sc-cream"
            }`}
          >
            <Icon size={18} /> {t.label}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-sc-blue flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-sc-espresso p-6 sticky top-0 h-screen">
        <div className="flex items-center gap-2.5 mb-10">
          <span className="grid place-items-center w-10 h-10 rounded-full bg-sc-cream text-sc-espresso"><IceCream2 size={20} /></span>
          <span className="leading-none">
            <span className="block font-serif-display text-lg text-sc-cream">Santa Chiara</span>
            <span className="block eyebrow text-[0.55rem] mt-0.5 text-sc-goldlight">Admin</span>
          </span>
        </div>
        <Nav />
        <button onClick={handleLogout} data-testid="logout-btn" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-sc-cream/70 hover:bg-white/10">
          <LogOut size={18} /> Sign out
        </button>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-sc-espresso p-6 flex flex-col">
            <button onClick={() => setMobileOpen(false)} className="self-end text-sc-cream mb-6"><X /></button>
            <Nav />
            <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-sc-cream/70 hover:bg-white/10"><LogOut size={18} /> Sign out</button>
          </div>
          <div className="flex-1 bg-black/40" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      <main className="flex-1 min-w-0">
        <header className="md:hidden flex items-center justify-between bg-sc-espresso px-5 py-4">
          <span className="font-serif-display text-lg text-sc-cream">Santa Chiara Admin</span>
          <button onClick={() => setMobileOpen(true)} className="text-sc-cream"><Menu /></button>
        </header>
        <div className="p-6 md:p-10">
          <p className="text-sm text-sc-espresso/50 mb-6 hidden md:block">Signed in as {user?.email}</p>
          <ActiveComp />
        </div>
      </main>
    </div>
  );
}
